import React from "react";
import { connect, ConnectedProps } from "react-redux";
import {
	Bar,
	CartesianGrid,
	Cell,
	ComposedChart,
	LabelList,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { Dispatch } from "redux";
import { IStore } from "redux/store";

interface DirectionArrowProps {
	direction: string;
	color: string;
}

interface ICustomizedAxisTickProps {
	x?: number;
	y?: number;
	payload?: {
		value: string;
	};
}

const CustomizedAxisTick: React.FC<ICustomizedAxisTickProps> = ({
	x,
	y,
	payload,
}) => {
	if (x === undefined || y === undefined || !payload) {
		return null;
	}
	const dateObj = new Date(payload.value);
	const timeFormatter = new Intl.DateTimeFormat("en-GB", {
		hour: "2-digit",
		minute: "2-digit",
		timeZone: "UTC",
	});
	const dateFormatter = new Intl.DateTimeFormat("en-US", {
		month: "short",
		day: "2-digit",
		timeZone: "UTC",
	});

	const time = timeFormatter.format(dateObj);
	const date = dateFormatter.format(dateObj);

	return (
		<g transform={`translate(${x},${y + 20})`}>
			<text x={0} y={0} dy={16} textAnchor="middle" fill="#666" fontSize="12px">
				<tspan x="0" dy="0.2em">
					{time}
				</tspan>
				<tspan x="0" dy="1.2em">
					{date}
				</tspan>
			</text>
		</g>
	);
};

interface CustomTooltipProps {
	active?: boolean;
	payload?: any[];
	label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
	active,
	payload,
	label,
}) => {
	if (active && payload && payload.length && label) {
		const dateObj = new Date(label);
		const timeFormatter = new Intl.DateTimeFormat("en-GB", {
			hour: "2-digit",
			minute: "2-digit",
			timeZone: "UTC",
		});
		const dateFormatter = new Intl.DateTimeFormat("en-US", {
			month: "short",
			day: "2-digit",
			year: "numeric",
			timeZone: "UTC",
		});

		const time = timeFormatter.format(dateObj);
		const date = dateFormatter.format(dateObj);

		return (
			<div className="bg-white p-3 border border-gray-200 rounded-lg shadow-xl text-sm">
				<p className="font-medium text-gray-800 mb-2">{`${date}, ${time}`}</p>
				{payload.map((pld, index) => (
					<p key={index} style={{ color: pld.fill }}>
						<span className="font-medium">{`${pld.name}: `}</span>
						<span>{`${pld.value} ${pld.unit || ""}`}</span>
					</p>
				))}
			</div>
		);
	}
	return null;
};

const directionRotationMap: { [key: string]: number } = {
	N: 0,
	NE: 45,
	E: 90,
	SE: 135,
	S: 180,
	SW: 225,
	W: 270,
	NW: 315,
};

interface IChartItem {
	dateTime: string;
	swellHeight: number | null;
	swellDir: string;
	swellDir2: string;
	windSpeed: number | null;
	windDir: string;
	windDir2: string;
	currentFactor: number | null;
	type: "synopsis" | "forecast";
	name: string;
}

const weatherReportingChartData: Omit<IChartItem, "name">[] = [
	{
		dateTime: "2025-05-01T17:00:00Z",
		swellHeight: 1.3,
		swellDir: "S",
		swellDir2: "SW",
		windSpeed: 4,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: -3,
		type: "synopsis",
	},
	{
		dateTime: "2025-05-02T16:00:00Z",
		swellHeight: 3.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 5,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: 5,
		type: "synopsis",
	},
	{
		dateTime: "2025-05-03T15:00:00Z",
		swellHeight: 1.3,
		swellDir: "W",
		swellDir2: "W",
		windSpeed: 3,
		windDir: "W",
		windDir2: "W",
		currentFactor: -6,
		type: "synopsis",
	},
	{
		dateTime: "2025-05-04T14:00:00Z",
		swellHeight: 3.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 6,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: 3,
		type: "synopsis",
	},
	{
		dateTime: "2025-05-05T12:00:00Z",
		swellHeight: 1.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 4,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: -4,
		type: "synopsis",
	},
	{
		dateTime: "2025-05-06T12:00:00Z",
		swellHeight: 3.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: null,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: 4,
		type: "synopsis",
	},
	{
		dateTime: "2025-05-07T13:00:00Z",
		swellHeight: 1.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 6,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: -5,
		type: "synopsis",
	},
	{
		dateTime: "2025-05-08T12:00:00Z",
		swellHeight: 3.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 3,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: 6,
		type: "synopsis",
	},
	{
		dateTime: "2025-05-09T14:00:00Z",
		swellHeight: 1.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 4,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: -3,
		type: "synopsis",
	},
	{
		dateTime: "2025-05-10T15:00:00Z",
		swellHeight: 3.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 5,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: 4,
		type: "synopsis",
	},
	{
		dateTime: "2025-05-11T12:00:00Z",
		swellHeight: 1.3,
		swellDir: "E",
		swellDir2: "E",
		windSpeed: 6,
		windDir: "E",
		windDir2: "E",
		currentFactor: -6,
		type: "forecast",
	},
	{
		dateTime: "2025-05-12T12:00:00Z",
		swellHeight: 3.3,
		swellDir: "E",
		swellDir2: "E",
		windSpeed: 3,
		windDir: "E",
		windDir2: "E",
		currentFactor: 3,
		type: "forecast",
	},
	{
		dateTime: "2025-05-14T14:00:00Z",
		swellHeight: 1.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 4,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: -4,
		type: "forecast",
	},
	{
		dateTime: "2025-05-15T17:00:00Z",
		swellHeight: 3.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 5,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: 5,
		type: "forecast",
	},
	{
		dateTime: "2025-05-16T11:00:00Z",
		swellHeight: 1.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 6,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: -6,
		type: "forecast",
	},
	{
		dateTime: "2025-05-17T17:00:00Z",
		swellHeight: 3.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 3,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: 3,
		type: "forecast",
	},
	{
		dateTime: "2025-05-18T14:00:00Z",
		swellHeight: 1.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 4,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: -4,
		type: "forecast",
	},
	{
		dateTime: "2025-05-19T15:00:00Z",
		swellHeight: 3.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 5,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: 5,
		type: "forecast",
	},
	{
		dateTime: "2025-05-21T11:00:00Z",
		swellHeight: 1.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 6,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: null,
		type: "forecast",
	},
	{
		dateTime: "2025-05-22T12:00:00Z",
		swellHeight: 3.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 4,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: -4,
		type: "forecast",
	},
	{
		dateTime: "2025-05-23T12:00:00Z",
		swellHeight: 1.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 5,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: 5,
		type: "forecast",
	},
	{
		dateTime: "2025-05-24T12:00:00Z",
		swellHeight: 3.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 3,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: -3,
		type: "forecast",
	},
	{
		dateTime: "2025-05-26T12:00:00Z",
		swellHeight: 1.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 4,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: 4,
		type: "forecast",
	},
	{
		dateTime: "2025-05-27T12:00:00Z",
		swellHeight: 3.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 6,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: -6,
		type: "forecast",
	},
	{
		dateTime: "2025-05-28T14:00:00Z",
		swellHeight: 1.3,
		swellDir: "SW",
		swellDir2: "SW",
		windSpeed: 3,
		windDir: "SW",
		windDir2: "SW",
		currentFactor: 3,
		type: "forecast",
	},
];

interface DirectionArrowRowProps {
	label: string;
	data: IChartItem[];
	dirKey: keyof IChartItem;
	dirKey2: keyof IChartItem;
	barWidth: number;
	yAxisWidth: number;
	chartMargin: {
		top: number;
		right: number;
		left: number;
		bottom: number;
	};
}

const DirectionArrow: React.FC<DirectionArrowProps> = ({
	direction,
	color,
}) => {
	const rotation = directionRotationMap[direction] || 0;
	return (
		<svg
			width="18"
			height="18"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			style={{
				transform: `rotate(${rotation}deg)`,
				transition: "transform 0.2s ease",
			}}
		>
			<path
				d="M12 2L12 18"
				stroke={color}
				strokeWidth="2.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M5 9L12 2L19 9"
				stroke={color}
				strokeWidth="2.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
};
const DirectionArrowRow: React.FC<DirectionArrowRowProps> = ({
	label,
	data,
	dirKey,
	dirKey2,
	barWidth,
	yAxisWidth,
	chartMargin,
}) => {
	return (
		<div className="flex w-full h-16">
			<div
				style={{ width: `${yAxisWidth + chartMargin.left}px` }}
				className="flex-shrink-0 flex items-center justify-center text-xs text-gray-500 transform -rotate-90"
			>
				<div
					className="text-[12px]"
					style={{ width: "max-content", color: "#344054" }}
				>
					{label}
				</div>
			</div>
			<div className="flex-grow flex">
				{data.map((entry, index) => {
					const color1 = "#4a4a4a";
					const color2 = "#ef4444"; // Red for the second arrow
					const dir1 = entry[dirKey] as string;
					const dir2 = entry[dirKey2] as string;
					return (
						<div
							key={`arrow-col-${index}`}
							className="flex flex-col justify-around items-center py-1"
							style={{ width: `${barWidth}px` }}
						>
							<DirectionArrow direction={dir1} color={color1} />
							<DirectionArrow direction={dir2} color={color2} />
						</div>
					);
				})}
			</div>
			<div
				style={{ width: `${chartMargin.right}px` }}
				className="flex-shrink-0"
			></div>
		</div>
	);
};

type OwnProps = {};
type WeatherReportingChartProps = PropsFromRedux & OwnProps;
function WeatherReportingChart(props: WeatherReportingChartProps) {
	const data = weatherReportingChartData.map((d) => ({
		...d,
		name: d.dateTime,
	}));

	const barWidth = 60;
	const yAxisWidth = 80;
	const chartWidth = data.length * barWidth + yAxisWidth + 20;
	const chartMargin = { top: 20, right: 20, left: 0, bottom: 5 };
	const syncId = "syncedCharts";

	return (
		<div className="mt-4">
			<div className="bg-white rounded-md shadow-md p-4 sm:p-6 border border-gray-300">
				<div className="flex justify-end items-center mb-6 text-sm text-gray-600">
					<div className="flex items-center mr-4">
						<div className="w-4 h-4 bg-blue-500 mr-2 rounded-sm"></div>
						<span>Synopsis</span>
					</div>
					<div className="flex items-center">
						<div className="w-4 h-4 bg-[#f59e0b] mr-2 rounded-sm"></div>
						<span>
							Forecast *Direction relative to the ship's head. Head up
						</span>
					</div>
				</div>
				<div className="w-full overflow-x-auto ws-scroll min-h-[550px] relative">
					<div style={{ width: `${chartWidth}px` }}>
						<div className="border-[#D0D5DD] border-l absolute h-[500px] left-[79.5px] w-1" />
						<div className="w-full h-[150px]">
							<ResponsiveContainer>
								<ComposedChart data={data} margin={chartMargin} syncId={syncId}>
									<CartesianGrid
										strokeDasharray="3 3"
										stroke="#8884d8"
										vertical={true}
										horizontal={true}
									/>
									<XAxis dataKey="name" tick={false} stroke="#344054" />
									<YAxis
										yAxisId="left"
										width={yAxisWidth}
										domain={[0, 5]}
										stroke="#D0D5DD"
										label={{
											value: "Swell Height (m)",
											angle: -90,
											position: "",
											offset: -35,
											style: {
												textAnchor: "middle",
												fill: "#344054",
												fontSize: "12px",
											},
										}}
										tickLine={false}
										tick={false}
									/>
									<Tooltip content={<CustomTooltip />} />
									<Bar dataKey="swellHeight" yAxisId="left" barSize={30}>
										<LabelList
											dataKey="swellHeight"
											position="top"
											style={{ fill: "#333", fontSize: "12px" }}
										/>
										{data.map((entry, index) => (
											<Cell
												key={`cell-${index}`}
												fill={entry.type === "synopsis" ? "#3b82f6" : "#f59e0b"}
											/>
										))}
									</Bar>
								</ComposedChart>
							</ResponsiveContainer>
						</div>
						<DirectionArrowRow
							label="Swell dir*"
							data={data}
							dirKey="swellDir"
							dirKey2="swellDir2"
							barWidth={barWidth}
							yAxisWidth={yAxisWidth}
							chartMargin={chartMargin}
						/>
						<div className="w-full h-[200px]">
							<ResponsiveContainer>
								<ComposedChart data={data} margin={chartMargin} syncId={syncId}>
									<CartesianGrid strokeDasharray="3 3" vertical={true} />
									<XAxis dataKey="name" tick={false} axisLine={false} />
									<YAxis
										yAxisId="left"
										stroke="#D0D5DD"
										width={yAxisWidth}
										domain={[0, 8]}
										label={{
											value: "Wind Speed (Bft)",
											angle: -90,

											offset: -35,
											style: {
												textAnchor: "middle",
												fill: "#344054",
												fontSize: "12px",
											},
										}}
										tick={false}
									/>
									<Tooltip content={<CustomTooltip />} />
									<Bar dataKey="windSpeed" yAxisId="left" barSize={30}>
										<LabelList
											dataKey="windSpeed"
											position="top"
											style={{ fill: "#333", fontSize: "12px" }}
										/>
										{data.map((entry, index) => (
											<Cell
												key={`cell-${index}`}
												fill={entry.type === "synopsis" ? "#3b82f6" : "#f59e0b"}
											/>
										))}
									</Bar>
								</ComposedChart>
							</ResponsiveContainer>
						</div>
						<DirectionArrowRow
							label="Wind dir*"
							data={data}
							dirKey="windDir"
							dirKey2="windDir2"
							barWidth={barWidth}
							yAxisWidth={yAxisWidth}
							chartMargin={chartMargin}
						/>

						<div className="w-full h-[280px]">
							<ResponsiveContainer>
								<ComposedChart
									data={data}
									margin={{ ...chartMargin, bottom: 40 }}
									syncId={syncId}
								>
									<CartesianGrid strokeDasharray="3 3" vertical={true} />
									<XAxis
										dataKey="name"
										tickLine={false}
										axisLine={true}
										tick={<CustomizedAxisTick />}
										interval={0}
										height={50}
									/>
									<YAxis
										yAxisId="left"
										stroke="#D0D5DD"
										width={yAxisWidth}
										domain={[-10, 10]}
										label={{
											value: "Current factor (kts)",
											angle: -90,

											offset: -35,
											style: {
												textAnchor: "middle",
												fill: "#344054",
												fontSize: "12px",
											},
										}}
										tick={false}
									/>
									<Tooltip content={<CustomTooltip />} />
									<ReferenceLine
										yAxisId="left"
										y={0}
										stroke="#666"
										strokeDasharray="3 3"
									/>
									<Bar dataKey="currentFactor" yAxisId="left" barSize={30}>
										<LabelList
											dataKey="currentFactor"
											position="top"
											style={{ fill: "#333", fontSize: "12px" }}
										/>
										{data.map((entry, index) => (
											<Cell
												key={`cell-${index}`}
												fill={entry.type === "synopsis" ? "#3b82f6" : "#f59e0b"}
											/>
										))}
									</Bar>
								</ComposedChart>
							</ResponsiveContainer>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
function mapStateToProps(store: IStore, ownProps: OwnProps) {
	return {};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(WeatherReportingChart);
